import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CourseDesignChat } from "@/components/CourseDesignChat";
import LearnAboutPage from "@/pages/LearnAboutPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";

const LogoIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="15" stroke="#1C3829" strokeWidth="1.5" />
    <path
      d="M10 16 C10 11, 16 8, 22 12 C22 17, 16 24, 10 20 C10 18, 12 17, 16 16 C14 14.5, 11 14, 10 16Z"
      fill="#1C3829"
      opacity="0.8"
    />
    <circle cx="20" cy="12" r="2" fill="#1C3829" />
  </svg>
);

const Index = () => {
  const [learningTopic, setLearningTopic] = useState("");
  const [showChatDialog, setShowChatDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { userType, isLoading: typeLoading } = useUserType(session?.user?.id);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!session || typeLoading) return;
    if (userType === "learner") {
      navigate("/learn", { replace: true });
    }
    // Everyone else (designer, other_no_course, other_one_course): stay on / and show LearnAboutPage (course creation landing)
  }, [session, userType, typeLoading, navigate]);

  useEffect(() => {
    const handleAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const error = hashParams.get("error");
      const errorDescription = hashParams.get("error_description");

      if (error) {
        console.error("OAuth error:", error, errorDescription);
        window.history.replaceState({}, "", "/");
        return;
      }

      if (accessToken) {
        try {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || "",
          });

          if (sessionError) {
            console.error("Error setting session:", sessionError);
            return;
          }

          if (data.session) {
            window.history.replaceState({}, "", "/");
            navigate("/");
          }
        } catch (err) {
          console.error("Error handling auth callback:", err);
        }
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const handleGetStarted = () => {
    if (learningTopic.trim()) {
      setShowChatDialog(true);
    } else {
      navigate("/auth");
    }
  };

  return (
    <div className="landing">
      {session && !typeLoading && userType !== "learner" ? (
        <LearnAboutPage />
      ) : (
        <>
      <nav className="landing-nav">
        <Link to="/" className="nav-logo">
          <LogoIcon />
          Distilled Intuition
        </Link>
        <ul className="nav-links">
          <li><Link to="/#platform-capabilities">Features</Link></li>
          <li><Link to="/#showcase">Showcase</Link></li>
        </ul>
        <div className="nav-actions">
          <Link to="/auth" className="btn-ghost">Sign In</Link>
          <Link to="/request-demo" className="btn-primary">Request Demo</Link>
        </div>
      </nav>

      <section style={{ background: "var(--cream)" }}>
        <div className="hero">
          <div className="hero-left">
            <div className="eyebrow">
              <span className="eyebrow-dot" />
              The First AI-Native Learning Management System
            </div>
            <h1 className="hero-headline">
              Your Training,
              <br />
              <em>Supercharged</em>
            </h1>
            <p className="hero-sub">
              Manage your organization's learning from idea to assessment — and everything in between.
            </p>

            <div className="hero-search">
              <input
                type="text"
                placeholder="e.g., Project Management, Python, Leadership..."
                value={learningTopic}
                onChange={(e) => setLearningTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGetStarted()}
              />
              <button type="button" className="btn-search" onClick={handleGetStarted}>
                Get Started
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="hero-cta-row">
              <Link to="/request-demo" className="btn-outline">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <rect x="2" y="2" width="11" height="11" rx="2" stroke="#4A6B58" strokeWidth="1.3" />
                  <path d="M5 7.5h5M5 5h5M5 10h3" stroke="#4A6B58" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
                Request Enterprise Demo
              </Link>
            </div>
          </div>

          <div className="hero-right">
            <div className="float-card card-1">
              <div className="float-card-label">Completion Rate</div>
              <div className="float-card-value">87%</div>
              <div className="float-card-sub">↑ 23% this quarter</div>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" />
              </div>
            </div>
            <div className="float-card card-2">
              <div className="float-card-label">Nodes Complete</div>
              <div className="float-card-value">7</div>
              <div className="float-card-sub">of 12 skill nodes</div>
            </div>

            <div className="product-frame">
              <div className="product-topbar">
                <div className="product-dot red" />
                <div className="product-dot amber" />
                <div className="product-dot green" />
                <div className="product-url">app.distilledintuition.com/courses/defense-acquisition</div>
              </div>
              <div className="product-content">
                <div className="product-breadcrumb">
                  <svg viewBox="0 0 14 14" fill="none">
                    <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                  Back to Courses
                </div>
                <div className="product-title">Defense Acquisition Reform</div>
                <div className="product-subtitle">Deep dive into the new defense acquisition rules</div>
                <div className="product-tabs">
                  <div className="product-tab">Materials</div>
                  <div className="product-tab active">Skill Tree</div>
                  <div className="product-tab">Slides</div>
                  <div className="product-tab">Course Details</div>
                </div>
                <div className="product-skill-tree">
                  <div className="skill-tree-header">
                    <div>
                      <div className="skill-tree-title">Navigating New Rules</div>
                      <div className="skill-tree-meta">7 skill nodes across 3 levels</div>
                    </div>
                    <div className="skill-actions">
                      <button type="button" className="skill-btn primary">+ Add Node</button>
                      <button type="button" className="skill-btn secondary">✦ Regenerate</button>
                    </div>
                  </div>
                  <div className="skill-level-label">Level 1</div>
                  <div className="skill-cards-row">
                    <div className="skill-card">
                      <div className="skill-card-title">Introduction to Defense Acquisition</div>
                      <div className="skill-card-desc">
                        Understand foundational concepts, historical evolution, and traditional structure.
                      </div>
                      <div className="skill-card-edit">✎ Click to edit</div>
                    </div>
                    <div className="skill-card">
                      <span className="key-node-badge">Key Node</span>
                      <div className="skill-card-title">Key Stakeholders and Acquisition Process</div>
                      <div className="skill-card-desc">
                        Identify major governmental, industry, and legislative stakeholders.
                      </div>
                      <div className="skill-card-edit">✎ Click to edit</div>
                      <div className="prereq-tag">1 prerequisite(s)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showChatDialog && (
        <CourseDesignChat initialTopic={learningTopic} onClose={() => setShowChatDialog(false)} />
      )}

      <section id="platform-capabilities" className="features-section">
        <div className="section-eyebrow">Platform Capabilities</div>
        <h2 className="section-headline">
          Everything learning teams <em>actually</em> need
        </h2>
        <p className="section-sub">
          From AI-generated content to adaptive skill trees, Distilled Intuition handles the full lifecycle of organizational learning.
        </p>

        <div className="features-grid">
          <div className="feature-card dark">
            <div className="feature-icon">🧠</div>
            <div className="feature-card-title">AI Content Generation</div>
            <div className="feature-card-desc">
              Go from topic to full course in minutes. Our AI drafts objectives, modules, assessments, and slides — you refine and publish.
            </div>
            <div className="feature-tag">✦ AI-Powered</div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🌳</div>
            <div className="feature-card-title">Adaptive Skill Trees</div>
            <div className="feature-card-desc">
              Visualize prerequisite paths and knowledge dependencies. Learners unlock nodes as they progress through structured learning journeys.
            </div>
            <div className="feature-tag">→ Learner Experience</div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <div className="feature-card-title">Performance Analytics</div>
            <div className="feature-card-desc">
              Track completion rates, assessment scores, and engagement patterns across your entire learning portfolio in real time.
            </div>
            <div className="feature-tag">→ Analytics</div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <div className="feature-card-title">Smart Assessments</div>
            <div className="feature-card-desc">
              Auto-generate quizzes tied to learning objectives. Adaptive difficulty and instant feedback keep learners challenged at the right level.
            </div>
            <div className="feature-tag">→ Assessments</div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔗</div>
            <div className="feature-card-title">Deep Integrations</div>
            <div className="feature-card-desc">
              Connect with Slack, Teams, HRIS platforms, and your SSO provider. SCORM/xAPI support for legacy content included.
            </div>
            <div className="feature-tag">→ Integrations</div>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎨</div>
            <div className="feature-card-title">White-Label Ready</div>
            <div className="feature-card-desc">
              Custom branding, your domain, your colors. Deploy a learning portal that feels native to your organization's identity.
            </div>
            <div className="feature-tag">→ Enterprise</div>
          </div>
        </div>
      </section>

      <section id="showcase" className="features-section" style={{ paddingTop: "60px" }}>
        <div className="section-eyebrow">Product Showcase</div>
        <h2 className="section-headline">See Distilled Intuition in action</h2>
        <p className="section-sub">Ready to build your own custom syllabus? Jump into our course generation workflow to turn your topic into a complete, guided learning path.</p>
        <Link to="/auth" className="btn-primary" style={{ marginTop: "10px", display: "inline-flex" }}>
          Create Your Custom Syllabus
        </Link>
      </section>

      <section id="skill-tree-showcase" className="showcase-section">
        <div className="showcase-inner">
          <div className="showcase-left">
            <div className="section-eyebrow">Learner Experience</div>
            <h2 className="section-headline" style={{ color: "white", maxWidth: "none" }}>
              Learning that <em>maps</em> to mastery
            </h2>
            <p className="section-sub">
              Skill trees give learners a visual map of their journey. Completed nodes unlock new ones, creating momentum and a clear sense of progress.
            </p>
            <div className="showcase-stats">
              <div className="stat-box">
                <div className="stat-number">3×</div>
                <div className="stat-label">Higher completion vs traditional LMS</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">68%</div>
                <div className="stat-label">Reduction in content creation time</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">12min</div>
                <div className="stat-label">Average to publish a full course</div>
              </div>
            </div>
          </div>
          <div className="showcase-right">
            <div className="skill-tree-visual">
              <div className="stv-header">
                <div className="stv-title">Defense Acquisition Reform</div>
                <div className="stv-sub">Click on available nodes to view content and progress</div>
              </div>
              <div className="stv-timeline">
                <div className="stv-row">
                  <div className="stv-node done">✓</div>
                  <div className="stv-info">
                    <div className="stv-node-title">Introduction to Defense Acquisition</div>
                    <div className="stv-node-meta">Level 1 · Completed</div>
                  </div>
                </div>
                <div className="stv-row">
                  <div className="stv-node active">★</div>
                  <div className="stv-info">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div className="stv-node-title">Key Stakeholders & Acquisition Process</div>
                      <span className="stv-badge key">Key Node</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                      <div className="stv-node-meta">Level 1 · In progress</div>
                      <span className="stv-badge exam">Exam</span>
                    </div>
                  </div>
                </div>
                <div className="stv-row" style={{ opacity: 0.5 }}>
                  <div className="stv-node locked">🔒</div>
                  <div className="stv-info">
                    <div className="stv-node-title">Regulatory Framework Deep Dive</div>
                    <div className="stv-node-meta">Level 2 · Locked — 1 prerequisite</div>
                  </div>
                </div>
                <div className="stv-row" style={{ opacity: 0.35 }}>
                  <div className="stv-node locked">🔒</div>
                  <div className="stv-info">
                    <div className="stv-node-title">Contract Vehicles & Award Types</div>
                    <div className="stv-node-meta">Level 2 · Locked — 2 prerequisites</div>
                  </div>
                </div>
                <div className="stv-row" style={{ opacity: 0.2 }}>
                  <div className="stv-node locked">🔒</div>
                  <div className="stv-info">
                    <div className="stv-node-title">Advanced Acquisition Strategy</div>
                    <div className="stv-node-meta">Level 3 · Locked</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="cta-section">
        <h2 className="cta-headline">
          Ready to supercharge your <em>team's training?</em>
        </h2>
        <div className="cta-actions">
          <Link to="/auth" className="btn-white">Get Started Free</Link>
          <Link to="/request-demo" className="btn-white-outline">Request Enterprise Demo</Link>
        </div>
      </div>

      <footer className="landing-footer">
        <div className="footer-logo">
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="#1C3829" strokeWidth="1.5" />
            <path
              d="M10 16 C10 11, 16 8, 22 12 C22 17, 16 24, 10 20 C10 18, 12 17, 16 16 C14 14.5, 11 14, 10 16Z"
              fill="#1C3829"
              opacity="0.8"
            />
          </svg>
          Distilled Intuition
        </div>
        <ul className="footer-links">
          <li><Link to="/#platform-capabilities">Features</Link></li>
          <li><a href="#">Blog</a></li>
          <li><Link to="/privacy">Privacy</Link></li>
          <li><Link to="/terms">Terms</Link></li>
        </ul>
        <span className="footer-copy">© 2026 Distilled Intuition Inc.</span>
      </footer>
        </>
      )}
    </div>
  );
};

export default Index;
